import { useCallback, useEffect, useState } from 'react'

import { useKeenSlider } from 'keen-slider/react'

import { useGuideContext } from 'react-provider'
import BookCard from '@/components/BookCard'
import CardLayout from '@/components/CardLayout'
import GlobalCard from '@/components/GlobalCard'
import Icon from '@/components/Icon'
import { useCustomNavigate, useVisibilityReport } from '@/hooks'
import { isSuccess } from '@/axios'
import { INovelDetail, getDiscoverCommentReview, IDiscoverCommentReview, historyGetByResource } from '@/api'
import {
  CONTENT_VIEW_FROM_KEY,
  cn,
  customJumpHref,
  handleReadingButtonShowTrack,
  handleResourceClickTrack,
  handleResourceDisplayTrack,
  setCookieAuthInfo,
} from '@/utils'
import { useAtomValue } from 'jotai'
import { loginStateAtom, mobileStatusAtom, store, useGlobalLoading } from '@/store'
import { useDebounceFn } from 'ahooks'
import SkeletonState from '@/components/CommonState/SkeletonState'

interface PropsIn {
  className?: string
}

const DiscoverComment = (props: PropsIn) => {
  const { className } = props
  const [load, setLoad] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const { customNavigator } = useCustomNavigate()
  const [data, setData] = useState<IDiscoverCommentReview[]>([] as IDiscoverCommentReview[])
  const [currentSlide, setCurrentSlide] = useState(0)
  const [slidesCount, setSlidesCount] = useState(0)
  const isLogin = useAtomValue(loginStateAtom)
  const isMobile = useAtomValue(mobileStatusAtom)
  const { setGlobalLoading } = useGlobalLoading()
  const [sliderRef, instanceRef] = useKeenSlider({
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel)
    },
    created() {
      setLoad(isMobile)
    },
    slides: {
      perView: isMobile
        ? 'auto'
        : () => {
            const rootFontSize = document.documentElement.style.fontSize.split('px')[0]
            const containerClientWidth = document.querySelector('#discoverCommentSwiper')?.clientWidth
            const remSize = Number((Math.ceil(Number(rootFontSize) * 100) / 100).toFixed(1))
            const itemW = !store.get(mobileStatusAtom) && containerClientWidth ? containerClientWidth / 3 : remSize * 63.1 + remSize * 2.2

            const bodyW = containerClientWidth || remSize * 63.1
            return bodyW / itemW
          },
      spacing: () => {
        const rootFontSize = document.documentElement.style.fontSize.split('px')[0]

        const remSize = (Math.ceil(Number(rootFontSize) * 100) / 100).toFixed(1)
        return Number(remSize) * 2.2
      },
    },
  })

  const { reportReady } = useGuideContext()

  const getData = useCallback(async () => {
    setIsLoading(true)
    const resp = await getDiscoverCommentReview()
    setIsLoading(false)
    if (isSuccess(resp)) {
      return setData(resp.data.list)
    }
  }, [])

  const handleRead = async (item: IDiscoverCommentReview) => {
    if (!Object.prototype.hasOwnProperty.call(item, 'resource_info')) {
      return customNavigator('/notFound')
    }

    if (isLogin) {
      try {
        setGlobalLoading(true)
        const resp = await historyGetByResource({
          resource_id: String(item.resource_id) || '',
          resource_type: Number(item.resource_type),
        })

        if (isSuccess(resp) && resp.data.is_history) {
          return customJumpHref(`/detail/novel/${item.resource_type}-${item.resource_id}-${resp.data.history_data.chapter_id}`)
        } else {
          customJumpHref(`/detail/novel/${item.resource_type}-${item.resource_id}`)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setGlobalLoading(false)
      }
    } else {
      customJumpHref(`/detail/novel/${item.resource_type}-${item.resource_id}`)
    }
  }

  useEffect(() => {
    if (instanceRef.current && data?.length) {
      instanceRef.current.update()
      setSlidesCount(instanceRef.current.track.details.slides.length)
    }
  }, [data])

  useEffect(() => {
    if (data.length) {
      handleResourceDisplayTrack({
        book_review_show: currentSlide + 1,
        resource_id: data[currentSlide].resource_id,
        resource_genre: data[currentSlide].resource_info?.tag_id ?? '',
        resource_tag_id: data[currentSlide].resource_info?.plot_info?.map((item) => item.plot_id)?.join(',') || '',
      })
    }
  }, [currentSlide, data])

  useEffect(() => {
    getData()
  }, [])

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        getData()
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  const { run: handleResize } = useDebounceFn(
    () => {
      instanceRef.current?.update()
      setLoad(isMobile)
    },
    { wait: 500 },
  )

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useVisibilityReport('bookReview', () => {
    handleResourceDisplayTrack({
      book_review_show: currentSlide + 1, // Todo: pc端因为3个一起展示或者多个一起展示出来怎么赋值
      resource_id: data[currentSlide].resource_id,
      resource_genre: data[currentSlide].resource_info?.tag_id ?? '',
      resource_tag_id: data[currentSlide].resource_info?.plot_info?.map((item) => item.plot_id)?.join(',') || '',
    })
  })

  useVisibilityReport('discoverCommentBookReadBtn', () => {
    handleReadingButtonShowTrack({
      from: 6,
    })
  })

  if (isLoading) {
    return (
      <CardLayout rootClassName={cn('relative', className)} title='Book Review'>
        <SkeletonState type={['comment']} />
      </CardLayout>
    )
  }

  return (
    <>
      {data?.length ? (
        <CardLayout
          rootClassName={cn('relative', className)}
          title='Book Review'
          ref={() => {
            reportReady('commentBox')
          }}
          id='bookReview'
        >
          <div
            className={cn(
              'overflow-hidden px-[30px] md:px-0',
              `before:absolute before:left-0 before:top-0 before:z-[1] before:block before:h-full before:w-[30px] before:bg-base-color before:content-[''] md:before:w-0`,
            )}
            id='discoverCommentSwiper'
          >
            <div ref={sliderRef} className={cn('keen-slider relative !overflow-visible')}>
              {data.map((item, index) => {
                return (
                  <div className={cn('keen-slider__slide cursor-pointer', isMobile ? '!min-w-[631px]' : '')} key={`${item.id}_${index}`}>
                    <GlobalCard
                      onClick={() => {
                        handleResourceClickTrack({
                          book_review_enter: item.resource_comment_id,
                          resource_id: item.resource_id ?? '',
                          resource_genre: item.resource_info?.tag_id ?? '',
                          resource_tag_id: item.resource_info?.plot_info?.map((item) => item.plot_id)?.join(',') || '',
                          button: '1',
                        })
                        customNavigator(`/detail/review/${item.resource_type}-${item.resource_comment_id}`)
                      }}
                      userData={item}
                      className={cn('h-[522px] w-full')}
                      extra={
                        <div className={cn('flex items-center')}>
                          <Icon className={cn('mr-[9px] flex h-[22px] w-[22px] items-center justify-center')} name='HeartSmall'></Icon>
                          <span className='font-geologica text-[19px] font-[400] text-color-white-trans40'>
                            {!item.like_num ? 'Like' : item.like_num}
                          </span>
                        </div>
                      }
                    >
                      <div>
                        <div
                          className={cn(
                            'relative mb-[30px] line-clamp-2 h-[82px] w-full text-ellipsis break-words font-geologica text-[26px] font-normal leading-[41px] text-color-white-trans70',
                          )}
                        >
                          {item.content}
                          <div
                            className={cn(
                              'absolute bottom-0 right-0 z-10 flex h-[47px] w-[153px] items-center justify-between text-right font-geologica text-[26px] font-normal leading-[41px] text-base-follow-btn-color',
                              item.content!.length > 80 ? 'flex' : 'hidden',
                            )}
                          >
                            <span className={cn('inline-block h-full w-[88px] bg-discover-more-mask-bg-color')}></span>
                            <span className='flex h-full items-center bg-discover-more-bg-color pt-[4px]'>More</span>
                          </div>
                        </div>
                        <div className={cn('h-[271px] w-full rounded-[20px] bg-discover-card-content-bg-color p-[30px]')}>
                          <BookCard
                            className={cn('relative h-full w-full')}
                            pictureClassName={cn('h-[210px] w-[149px] rounded-[8px]')}
                            infoClassName={cn(
                              'm-0 ml-[19px] h-full flex-1 [&>span:first-child]:mb-[17px] [&>span:first-child]:max-h-[75px] [&>span:first-child]:h-auto [&>span:first-child]:whitespace-normal',
                            )}
                            data={(item?.resource_info as INovelDetail) || ([] as unknown as INovelDetail)}
                            options={
                              <button
                                className={cn(
                                  'absolute bottom-0 right-0 flex h-[45px] w-[86px] items-center justify-center rounded-[7px] !border-0 bg-button-bg p-0 font-geologica text-[22px] font-normal text-button-text-color',
                                )}
                                onClick={async (e) => {
                                  e.stopPropagation()
                                  handleResourceClickTrack({
                                    book_review_enter: item.resource_comment_id,
                                    resource_id: item.resource_id ?? '',
                                    resource_genre: item.resource_info?.tag_id ?? '',
                                    resource_tag_id: item.resource_info?.plot_info?.map((item) => item.plot_id)?.join(',') || '',
                                    button: '2',
                                  })
                                  // 设置content_view埋点来源
                                  setCookieAuthInfo(CONTENT_VIEW_FROM_KEY, JSON.stringify(7))
                                  handleRead(item)
                                }}
                                id='discoverCommentBookReadBtn'
                              >
                                Read
                              </button>
                            }
                          />
                        </div>
                      </div>
                    </GlobalCard>
                  </div>
                )
              })}
            </div>
            <div className={cn('absolute bottom-[-60px] left-1/2 flex -translate-x-1/2')}>
              {load &&
                instanceRef.current &&
                [...Array(slidesCount).keys()].map((idx) => {
                  return (
                    <div
                      className={cn(
                        'mx-[11px] h-[11px] rounded-[6px]',
                        currentSlide === idx ? 'w-[22px] bg-base-dolist-active-color' : 'w-[11px] bg-base-dolist-bg-color',
                      )}
                      key={idx}
                      onClick={() => {
                        instanceRef.current?.moveToIdx(idx)
                      }}
                    ></div>
                  )
                })}
            </div>
          </div>
        </CardLayout>
      ) : null}
    </>
  )
}

export default DiscoverComment
